import { API_BASE_URL } from "../utils/apiConfig";
import { apiClient } from "../utils/apiClient";

export type ContentType = "COURSE" | "MODULE" | "FEATURED" | "VIDEO" | "LINK" | "PDF";

export type CourseEventType = "VIDEO_OPEN" | "VIDEO_COMPLETE" | "LINK_CLICK" | "PDF_DOWNLOAD";

/** WordPress content tab ids — must stay in sync with CourseInsightsService.TAB_LABELS on the server. */
export const WORDPRESS_TAB = {
    COLLEGE_PLANNING: 3,
    NIL_EDU: 4,
    CONNECT: 5,
    DISCOVER_U: 6,
    SPOTLIGHT: 7,
    RESOURCES: 10,
    TUTORING: 11,
    ADVERTISING: 12,
} as const;

export interface CourseEventPayload {
    wordpressTab: number;
    courseId?: number | null;
    moduleId?: number | null;
    contentId: number;
    contentType: ContentType;
    eventType: CourseEventType;
}

/** Context passed to VideoLightbox so opens/completions are attributed in Course Insights. */
export type VideoAnalytics = Omit<CourseEventPayload, "eventType">;

export function courseVideoAnalytics(tab: number, courseId: number, contentType: ContentType = "COURSE"): VideoAnalytics {
    return { wordpressTab: tab, courseId, contentId: courseId, contentType };
}

export function moduleVideoAnalytics(tab: number, courseId: number, moduleId: number): VideoAnalytics {
    return { wordpressTab: tab, courseId, moduleId, contentId: moduleId, contentType: "MODULE" };
}

export function featuredVideoAnalytics(tab: number, contentId: number): VideoAnalytics {
    return { wordpressTab: tab, contentId, contentType: "FEATURED" };
}

export function standaloneVideoAnalytics(tab: number, contentId: number): VideoAnalytics {
    return { wordpressTab: tab, contentId, contentType: "VIDEO" };
}

/**
 * Records a content-consumption event for the current user. Fire-and-forget: analytics must never
 * block or break the user experience, so failures are swallowed (the server is the source of truth
 * and a missed event is acceptable).
 */
export function recordCourseEvent(payload: CourseEventPayload): void {
    try {
        void apiClient(`${API_BASE_URL}/api/course-events`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify(payload),
        }).catch(() => {
            // Intentionally ignored; analytics is best-effort.
        });
    } catch {
        // Intentionally ignored; analytics is best-effort.
    }
}
